import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { apiClient } from "../lib/site";

export default function InsightDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <Section testId="insight-not-found">
        <p className="overline mb-4">Not found</p>
        <h1 className="font-serif-display text-5xl text-white font-light mb-8">Article not found.</h1>
        <Link to="/insights" className="btn-secondary">Back to all insights</Link>
      </Section>
    );
  }
  if (!post) return <Section><p className="text-zinc-500">Loading…</p></Section>;

  return (
    <article data-testid="insight-detail-page">
      <SeoHead
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.cover_image}
        type="article"
      />
      <section className="relative px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16 border-b border-white/5">
        <Link
          to="/insights"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition text-sm mb-12"
          data-testid="insight-back-link"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> All insights
        </Link>
        <motion.p {...fadeUp} className="overline mb-6">
          {post.category} · {post.read_time}
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="font-serif-display font-light text-white leading-[1.05] tracking-tight max-w-5xl"
          style={{ fontSize: "clamp(36px, 6vw, 80px)", letterSpacing: "-0.025em" }}
        >
          {post.title}
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 text-zinc-400 text-base"
        >
          By {post.author} · {post.date}
        </motion.p>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-16">
        <div className="aspect-[16/9] overflow-hidden bg-[#0A0A0C] max-w-6xl">
          <img src={post.cover_image} alt="" className="w-full h-full object-cover opacity-90" />
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="max-w-3xl">
          <p className="text-zinc-200 text-xl md:text-2xl leading-relaxed mb-10 font-serif-display italic font-light">
            {post.excerpt}
          </p>
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-zinc-300 text-base md:text-lg leading-[1.8] mb-6">
              {para}
            </p>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 px-6 md:px-12 lg:px-16 py-24 bg-[#0A0A0C]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(28px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}
            >
              Want to talk to the team that wrote this?
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="insight-cta">
              Start a Conversation <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
